import type { DeltaBase, Unit } from '@ops/contracts'

import { cx } from '../lib/cx'
import { formatBaseDelta, formatDelta, tendenciaDe } from '../lib/format'
import { Icon, type IconName } from './Icon'

/**
 * Variación contra el período anterior, con **su base publicada al lado**.
 *
 * Regla de producto: prohibido un porcentaje cuya base no esté en la pantalla.
 * Un "+21,7%" sin decir contra qué es inauditable, y un panel inauditable no se
 * usa. Por eso `base` no es decorativa: cuando falta, el componente lo dice.
 *
 * El signo va con el menos tipográfico (−, U+2212), no con el guion.
 *
 * El tono es neutro salvo que le pases `direccion`: un delta solo no sabe si
 * crecer está bien (MRR) o mal (churn). Esa semántica la aporta la métrica.
 */
export interface DeltaTagProps {
  deltaPct: number | null
  /** Base del delta: período y valor contra el que se compara. */
  base?: DeltaBase | null
  /** Unidad de la base, para formatear su valor. */
  unidad?: Unit
  /** Con esto el delta se tiñe; sin esto queda en gris. */
  direccion?: 'higher_better' | 'lower_better'
  /** false esconde el texto de la base (cuando ya está en otra parte). */
  mostrarBase?: boolean
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

const ICONO: Record<'sube' | 'baja' | 'plano', IconName> = {
  sube: 'sube',
  baja: 'baja',
  plano: 'plano',
}

const SIZE: Record<NonNullable<DeltaTagProps['size']>, string> = {
  sm: 'text-micro',
  md: 'text-meta',
  lg: 'text-titulo',
}

export function DeltaTag({
  deltaPct,
  base,
  unidad = 'percent',
  direccion,
  mostrarBase = true,
  size = 'md',
  className,
}: DeltaTagProps) {
  const texto = formatDelta(deltaPct)
  if (texto === null) return null

  const tendencia = tendenciaDe(deltaPct)
  const bueno =
    direccion === undefined || tendencia === 'plano' || tendencia === 'sin-dato'
      ? null
      : (direccion === 'higher_better') === (tendencia === 'sube')

  const tono =
    bueno === null
      ? 'text-muted-strong'
      : bueno
        ? 'text-accent-text'
        : 'text-danger-text'

  const baseTexto = formatBaseDelta(base, unidad)

  return (
    <span
      data-testid="delta-tag"
      data-tendencia={tendencia}
      className={cx('inline-flex flex-wrap items-baseline gap-1.5', className)}
    >
      <span
        data-testid="delta-tag-valor"
        className={cx(
          'inline-flex items-center gap-1 font-semibold tabular',
          SIZE[size],
          tono,
        )}
      >
        {tendencia !== 'sin-dato' ? (
          <Icon name={ICONO[tendencia]} size={size === 'lg' ? 14 : 11} />
        ) : null}
        {texto}
      </span>
      {mostrarBase ? (
        <span
          data-testid="delta-tag-base"
          className="text-micro tabular text-muted-strong"
        >
          {baseTexto ?? 'base sin publicar'}
        </span>
      ) : null}
    </span>
  )
}
